import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, FileText, Loader2, CheckCircle, AlertTriangle, Building2, Scale, Users, Wrench, ClipboardList, FileSignature, FolderPlus, PackageOpen, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const fmtMoney = (n) => n == null ? '—' : 'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2 });

const uploadBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', background: 'hsl(var(--primary))', color: 'white', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' };

const CAT_ICON = {
    'Capacidad legal': Scale,
    'Experiencia del postor': ClipboardList,
    'Experiencia del personal clave': Users,
    'Experiencia del personal no clave': Users,
    'Formación académica del personal clave': Users,
    'Equipamiento estratégico': Wrench,
    'Capacidad financiera': Building2,
};

export default function BasesInput() {
    const { getAuthHeaders } = useAuth();
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);   // { bases, budget?, filesFound? } o forma antigua { data }
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const basesData = result?.bases?.data || result?.data || null;
    const basesWarnings = result?.bases?.warnings || result?.warnings || [];
    const budgetData = result?.budget || null;
    const filesFound = result?.filesFound || null;

    const handleSaveExpediente = async () => {
        if (!basesData) return;
        setSaving(true);
        setError('');
        try {
            const headers = await getAuthHeaders();
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            // Si el presupuesto vino en el ZIP, lo persistimos ya en el expediente
            // (Sobre Económico precargado — no se vuelve a pedir).
            const economico = budgetData?.ok ? {
                presupuestoTotal: budgetData.stats?.sum ?? null,
                verificado: budgetData.stats?.verified ?? false,
                costoDirecto: budgetData.stats?.costoDirecto ?? null,
                ocr: /OCR/i.test(budgetData.stats?.method || ''),      // leído de escaneo → aproximado
                chapters: budgetData.data?.chapters || [], // partidas para el motor de %
            } : null;
            const res = await fetch(`${API_URL}/api/expedientes`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...basesData, economico }),
            });
            const data = await res.json();
            if (data.ok) navigate(`/expediente/${data.expediente.id}`);
            else setError(data.error || 'No se pudo crear el expediente.');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (e) => {
        // Acepta selección múltiple o una carpeta entera; nos quedamos solo con
        // PDFs, Excels y ZIPs (el resto se ignora en el servidor de todos modos).
        const list = Array.from(e.target.files || []).filter(f => /\.(zip|pdf|xlsx?)$/i.test(f.name));
        if (list.length) { setFiles(list); setResult(null); setError(''); }
        e.target.value = '';
    };

    const handleUpload = async () => {
        if (!files.length) return;
        setLoading(true);
        setError('');
        setResult(null);

        const formData = new FormData();
        files.forEach(f => formData.append('files', f));

        try {
            const headers = await getAuthHeaders();
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            const res = await fetch(`${API_URL}/api/proceso/import`, { method: 'POST', headers, body: formData });
            const data = await res.json();
            if (data.ok) setResult(data);
            else setError(data.error || 'Error al procesar los archivos.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const { proceso, anexos, requisitos } = basesData || {};

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>1. Bases Integradas → Checklist del Expediente</h1>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Sube el PDF de las Bases Integradas. El GEM extrae automáticamente qué documentos exige el proceso para no ser descalificado.
                </p>
            </div>

            <div className="card" style={{ padding: '2rem', textAlign: 'center', border: '2px dashed hsl(var(--border))', marginBottom: '1.5rem' }}>
                <UploadCloud size={40} style={{ color: 'hsl(var(--primary))', margin: '0 auto 1rem' }} />
                <h3>Bases y presupuesto — sube lo que tengas</h3>
                <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                    Tal como baja de OSCE: un ZIP (aunque traiga ZIPs adentro), varios archivos sueltos, o una carpeta entera.
                    El sistema encuentra solo las Bases (PDF) y el presupuesto (Excel o PDF).
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', margin: '0.5rem 0 0.75rem' }}>
                    <label style={uploadBtn}>
                        Seleccionar archivos
                        <input type="file" multiple accept=".zip,.pdf,.xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                    <label style={{ ...uploadBtn, background: 'transparent', color: 'hsl(var(--primary))', border: '1px solid hsl(var(--primary))' }}>
                        Subir una carpeta
                        <input type="file" webkitdirectory="" directory="" multiple onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                </div>
                {files.length > 0 && (
                    <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                        {files.length === 1 ? files[0].name : `${files.length} archivos seleccionados`}
                        {' '}({(files.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB)
                    </p>
                )}
                <button
                    onClick={handleUpload}
                    disabled={!files.length || loading}
                    style={{
                        marginTop: '1rem', padding: '0.7rem 1.6rem',
                        // En "Procesando" mantenemos el azul (contraste), no el gris disabled.
                        backgroundColor: loading ? 'hsl(var(--primary))' : (!files.length ? 'hsl(var(--muted))' : 'hsl(var(--primary))'),
                        color: !files.length && !loading ? 'hsl(var(--muted-foreground))' : 'white',
                        border: 'none', borderRadius: 'var(--radius)',
                        cursor: !files.length || loading ? 'not-allowed' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600,
                    }}
                >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Procesando…</> : 'Analizar con GEM'}
                </button>
                {loading && (
                    <div style={{ marginTop: '1.1rem' }}>
                        <div className="kreo-bar"><div className="kreo-bar-fill" /></div>
                        <p style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                            Extrayendo archivos → leyendo las Bases con GEM → procesando el presupuesto. Puede tardar 30–60s en expedientes grandes.
                        </p>
                    </div>
                )}
                {error && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 'var(--radius)', color: '#b91c1c', fontSize: '0.875rem' }}>
                        <AlertTriangle size={14} style={{ display: 'inline', marginRight: '0.4rem' }} />{error}
                    </div>
                )}
            </div>

            {/* Archivos detectados en ZIP */}
            {filesFound && (
                <div className="card" style={{ padding: '1rem 1.5rem', marginBottom: '1rem', borderLeft: '4px solid #8b5cf6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <PackageOpen size={16} color="#8b5cf6" />
                        <strong style={{ fontSize: '0.875rem' }}>Archivos detectados en el ZIP</strong>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {filesFound.map((f, i) => (
                            <div key={i} style={{ fontSize: '0.8rem', color: f.seleccionado ? 'inherit' : 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ padding: '0.1rem 0.4rem', borderRadius: '4px', backgroundColor: f.tipo === 'pdf' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)', color: f.tipo === 'pdf' ? '#b91c1c' : '#065f46', fontSize: '0.7rem', fontWeight: 600 }}>{f.tipo.toUpperCase()}</span>
                                <span style={{ fontFamily: 'monospace' }}>{f.ruta}</span>
                                {f.seleccionado
                                    ? <span style={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 600 }}>✓ {f.nota || 'procesado'}</span>
                                    : f.nota && <span style={{ fontSize: '0.7rem', fontStyle: 'italic', opacity: 0.7 }}>— {f.nota}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Resumen de presupuesto (solo en modo ZIP) */}
            {budgetData?.ok && (() => {
                const esOCR = /OCR/i.test(budgetData.stats?.method || '');
                return (
                <div className="card" style={{ padding: '1rem 1.5rem', marginBottom: '1rem', borderLeft: `4px solid ${esOCR ? '#f59e0b' : '#10b981'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <CheckCircle size={16} color={esOCR ? '#f59e0b' : '#10b981'} />
                        <strong style={{ fontSize: '0.875rem' }}>Presupuesto detectado{esOCR ? ' (leído por OCR)' : ''}</strong>
                        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                            {budgetData.stats?.totalChapters || 0} capítulos · {budgetData.stats?.totalItems || 0} partidas
                            {budgetData.stats?.sum ? ` · ${fmtMoney(budgetData.stats.sum)}` : ''}
                        </span>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: esOCR ? '#b45309' : 'hsl(var(--muted-foreground))' }}>
                        {esOCR
                            ? '⚠️ El presupuesto venía ESCANEADO (imagen) y se leyó con OCR — los valores son aproximados y pueden tener errores. Revisa/ajusta las partidas en el Sobre Económico antes de presentar.'
                            : 'El presupuesto quedará disponible en la sección Sobre Económico del expediente.'}
                    </p>
                </div>
                );
            })()}

            {/* Advertencias del parser (p.ej. requerimiento escaneado) */}
            {basesWarnings.length > 0 && (
                <div className="card" style={{ padding: '1rem 1.5rem', marginBottom: '1rem', borderLeft: '4px solid #f59e0b', backgroundColor: 'rgba(245,158,11,0.06)' }}>
                    {basesWarnings.map((w, i) => (
                        <div key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.82rem', color: '#92400e', alignItems: 'flex-start' }}>
                            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span>{w}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Datos del proceso */}
            {proceso && (
                <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem', borderLeft: '4px solid hsl(var(--primary))' }}>
                    <h3 style={{ marginBottom: '1rem' }}>{proceso.objeto || 'Proceso identificado'}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', fontSize: '0.875rem' }}>
                        <Field label="Entidad" value={proceso.entidad} />
                        <Field label="Nomenclatura" value={proceso.nomenclatura} />
                        <Field label="Sistema" value={proceso.sistemaContratacion} />
                        <Field label="Valor referencial" value={fmtMoney(proceso.valorReferencial)} />
                    </div>
                </div>
            )}

            {/* Grupos de requisitos */}
            {requisitos && (
                <>
                    <ReqGroup
                        icon={CheckCircle} color="#10b981"
                        title="Documentos de Admisión (obligatorios)"
                        subtitle="Si falta uno, la oferta NO es admitida."
                        items={requisitos.admision}
                        render={(x) => (
                            <ReqRow
                                key={x.letra}
                                code={x.letra ? x.letra + ')' : ''}
                                title={x.documento}
                                anexo={x.anexo}
                                tag={x.deCorresponder ? 'de corresponder' : (x.aplicaConsorcio ? 'consorcio' : null)}
                                anexos={anexos}
                            />
                        )}
                    />
                    <ReqGroup
                        icon={ClipboardList} color="#3b82f6"
                        title="Requisitos de Calificación"
                        subtitle="Lo que debes acreditar para calificar."
                        items={requisitos.calificacion}
                        render={(x, i) => {
                            const Ico = CAT_ICON[x.categoria] || ClipboardList;
                            return (
                                <ReqRow
                                    key={i}
                                    icon={Ico}
                                    code={x.categoria}
                                    title={x.documento}
                                    anexo={x.anexo}
                                    tag={x.montoRequerido ? fmtMoney(x.montoRequerido) : null}
                                    anexos={anexos}
                                />
                            );
                        }}
                    />
                    {requisitos.perfeccionamiento?.length > 0 && (
                        <ReqGroup
                            icon={FileSignature} color="#8b5cf6"
                            title="Para Perfeccionar el Contrato (si ganas)"
                            subtitle="Documentos post-buena pro."
                            items={requisitos.perfeccionamiento}
                            render={(x) => (
                                <ReqRow
                                    key={x.letra}
                                    code={x.letra ? x.letra + ')' : ''}
                                    title={x.documento}
                                    anexo={x.anexo}
                                    tag={x.deCorresponder ? 'de corresponder' : null}
                                    anexos={anexos}
                                />
                            )}
                        />
                    )}

                    {/* Catálogo de anexos */}
                    {anexos?.length > 0 && (
                        <div className="card" style={{ padding: '1.5rem' }}>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                <FileText size={18} /> Formatos / Anexos del proceso ({anexos.length})
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.5rem' }}>
                                {anexos.map(a => (
                                    <div key={a.numero} style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'hsl(var(--muted))', borderRadius: 'var(--radius)', fontSize: '0.8rem' }}>
                                        <strong style={{ color: 'hsl(var(--primary))', whiteSpace: 'nowrap' }}>Anexo {a.numero}</strong>
                                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>{a.titulo}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Barra de avance: crea el expediente y pasa al armado (paso 2) */}
            {basesData && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginTop: '2rem', padding: '1.25rem 1.5rem', borderTop: '1px solid hsl(var(--border))', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
                        Revisa el checklist. Al continuar se crea el expediente y pasas a cargar los documentos.
                    </span>
                    <button
                        onClick={handleSaveExpediente}
                        disabled={saving}
                        style={{
                            padding: '0.8rem 1.8rem', backgroundColor: saving ? 'hsl(var(--muted))' : 'hsl(var(--primary))',
                            color: 'white', border: 'none', borderRadius: 'var(--radius)', cursor: saving ? 'wait' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap',
                        }}
                    >
                        {saving ? <><Loader2 size={16} className="animate-spin" /> Creando expediente...</> : <>Siguiente: armar expediente <ArrowRight size={18} /></>}
                    </button>
                </div>
            )}

            {!result && !loading && (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
                    <FileText size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.4 }} />
                    <p>Sube las Bases Integradas para generar el checklist del expediente.</p>
                </div>
            )}
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem', marginBottom: '0.2rem' }}>{label}</div>
            <div style={{ fontWeight: '600' }}>{value || '—'}</div>
        </div>
    );
}

function ReqGroup({ icon: Icon, color, title, subtitle, items, render }) {
    return (
        <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <Icon size={18} color={color} />
                <h3>{title}</h3>
                <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>{items?.length || 0} ítems</span>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>{subtitle}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {items?.length ? items.map(render) : <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>No se detectaron ítems.</p>}
            </div>
        </div>
    );
}

function ReqRow({ icon: Icon, code, title, anexo, tag, anexos }) {
    const anexoTitle = anexo && anexos?.find(a => a.numero === anexo)?.titulo;
    return (
        <div style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', alignItems: 'flex-start' }}>
            {Icon && <Icon size={16} style={{ marginTop: '2px', color: 'hsl(var(--muted-foreground))', flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem' }}>
                    {code && <strong style={{ color: 'hsl(var(--primary))', marginRight: '0.4rem' }}>{code}</strong>}
                    {title}
                </div>
                {anexo && (
                    <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>
                        → Usar Anexo {anexo}{anexoTitle ? `: ${anexoTitle}` : ''}
                    </div>
                )}
            </div>
            {tag && (
                <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '999px', backgroundColor: 'rgba(245,158,11,0.15)', color: '#b45309', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {tag}
                </span>
            )}
        </div>
    );
}
